import React from 'react';
import { Card, Typography, Skeleton, Empty } from 'antd';
import { FireOutlined } from '@ant-design/icons';
import { useResponsive } from '../../hooks/useResponsive';
import { useCurrency } from '../../hooks/useCurrency';
import { useAppSelector } from '../../hooks/redux';
import { useGetTopSellingProductsQuery } from '../../services/reportsApi';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { useMoneyPrivacy } from '../../hooks/useMoneyPrivacy';

const { Text } = Typography;

const TopProductsCard: React.FC = () => {
  const { isMobile } = useResponsive();
  const { currencySymbol } = useCurrency();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { getLocalDate } = useRestaurantTimeZone();
  const { mask } = useMoneyPrivacy();

  // Fecha local del restaurante en yyyy-MM-dd; el backend la convierte a UTC
  // con la zona horaria del tenant.
  const today = getLocalDate();

  const { data: products, isLoading } = useGetTopSellingProductsQuery(
    {
      restaurantId: currentRestaurant?.id || '',
      fromDate: today,
      toDate: today,
      limit: 5,
    },
    {
      skip: !currentRestaurant?.id,
      refetchOnMountOrArgChange: true,
      pollingInterval: 60000,
    }
  );

  if (!currentRestaurant?.id) {
    return null;
  }

  const formatMoney = (value: number) =>
    `${currencySymbol} ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const maxQuantity = Math.max(...(products ?? []).map((p) => p.quantitySold), 1);

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FireOutlined style={{ marginRight: 8, color: 'var(--color-warning-text)' }} />
          <span>Top productos</span>
        </div>
      }
      extra={<Text type="secondary">Hoy</Text>}
      style={{ borderRadius: isMobile ? 8 : 12, height: '100%' }}
    >
      {isLoading || !products ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : products.length === 0 ? (
        <Empty description="Sin ventas de productos hoy" style={{ margin: '24px 0' }} />
      ) : (
        products.map((product, index) => (
          <div
            key={product.productId}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 0',
              borderBottom: index < products.length - 1 ? '1px solid var(--border-color)' : 'none',
            }}
          >
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 650, width: 14, flex: 'none' }}>
              {index + 1}
            </Text>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text
                style={{
                  fontSize: isMobile ? '12px' : '13px',
                  fontWeight: 600,
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {product.productName}
              </Text>
              <div
                style={{
                  height: 5,
                  borderRadius: 3,
                  backgroundColor: 'var(--bg-tertiary)',
                  marginTop: 5,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${(product.quantitySold / maxQuantity) * 100}%`,
                    height: '100%',
                    borderRadius: 3,
                    backgroundColor: 'var(--color-primary)',
                  }}
                />
              </div>
            </div>
            <div style={{ textAlign: 'right', flex: 'none' }}>
              <Text strong style={{ fontSize: isMobile ? '12px' : '13px', display: 'block' }}>
                {mask('otros', formatMoney(product.revenue))}
              </Text>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {product.quantitySold} vendidos
              </Text>
            </div>
          </div>
        ))
      )}
    </Card>
  );
};

export default TopProductsCard;
